<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'

import { 
  getAllPermissions, 
  getRolePermissions, 
  assignPermissionToRole, 
  removePermissionFromRole 
} from '../api/permissions.service'
import { getRoleById } from '@/modules/admin/roles/api/roles.service'

const route = useRoute()
const router = useRouter()

const roleId = ref(route.params.roleId)
const currentRole = ref(null)
const allPermissions = ref([])
const rolePermissions = ref([])
const loading = ref(false)

const isGodRole = computed(() => currentRole.value?.name === 'God')

const permissionLabels = {
  EditProducts: 'Редактирование товаров',
  ImportProductImages: 'Импорт изображений товаров',
  EditProductAttributes: 'Редактирование атрибутов товаров',
  EditProductTypes: 'Редактирование типов товаров',
  ViewUsers: 'Просмотр пользователей',
  ReviewRegistrations: 'Рассмотрение заявок на регистрацию',
  ManageRoles: 'Управление ролями',
  ManagePermissions: 'Управление разрешениями',
  EditBrand: 'Редактирование брендов',
  EditCategory: 'Редактирование категорий',
  ViewDiscount: 'Просмотр скидок',
  EditDiscount: 'Редактирование скидок',
  BlockUsers: 'Блокировка пользователей',
  DeleteUsers: 'Удаление пользователей',
  EditCharacteristicValues: 'Редактирование значений характеристик',
  EditVariantCharacteristics: 'Редактирование характеристик вариантов',
  EditNotifications: 'Управление уведомлениями',
  ManageHomePage: 'Управление главной страницей',
  ViewCart: 'Просмотр проектов',
  ViewRoom: 'Просмотр разделов проекта',
  EditCompanyDetails: 'Изменение деталей компании',
  ManageCompanyLogo: 'Управление логотипом компании',
  ViewHomePage: 'Просмотр главной страницы',
  EditCarousel: 'Изменение карусели',
  ChangeUserRole: 'Изменение роли пользователя',
  EditWeeklySelection: 'Изменение товаров недели',
  ChangeOrderStatus: 'Изменение статуса заказа',
  ViewManagersStats: 'Просмотр статистики менеджеров',
  ReceiveNewUserNotifications: 'Получение уведомлений о новых пользователях',
  ReceiveOrderNotifications: 'Получение уведомлений о заказах',
  AssignOrder: 'Назначение заказа',
}

function getPermissionLabel(name) {
  return permissionLabels[name] || name
}

async function loadData() {
  loading.value = true
  try {
    const requests = [getAllPermissions(), getRolePermissions(roleId.value)]
    if (roleId.value) {
      requests.push(getRoleById(roleId.value))
    }
    const [permissions, rolePerms, role] = await Promise.all(requests)
    allPermissions.value = permissions
    rolePermissions.value = rolePerms
    if (role) currentRole.value = role
  } catch (error) {
    console.error('Ошибка загрузки', error)
  } finally {
    loading.value = false
  }
}

function hasPermission(permissionId) {
  return rolePermissions.value.some(p => p.id === permissionId)
}

async function togglePermission(permission) {
  if (isGodRole.value) return

  const has = hasPermission(permission.id)
  
  try {
    if (has) {
      await removePermissionFromRole(roleId.value, permission.id)
      rolePermissions.value = rolePermissions.value.filter(p => p.id !== permission.id)
    } else {
      await assignPermissionToRole(roleId.value, permission.id)
      rolePermissions.value.push(permission)
    }
  } catch (error) {
    console.error('Ошибка', error)
  }
}

function goBack() {
  router.push('/admin/roles')
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <div style="max-width: 800px; margin: 0 auto; padding: 24px">
    <Button 
      label="Назад" 
      icon="pi pi-arrow-left" 
      severity="secondary" 
      @click="goBack" 
      style="margin-bottom: 20px"
    />

    <Card>
      <template #title>
        Разрешения роли
        <span v-if="isGodRole" style="margin-left: 12px; font-size: 0.75rem; color: #888; font-weight: normal; background: #f3f4f6; border-radius: 4px; padding: 2px 8px;">
          только просмотр
        </span>
      </template>

      <template #content>
        <div v-if="isGodRole" style="margin-bottom: 16px; padding: 10px 14px; background: #fff8e1; border: 1px solid #ffe082; border-radius: 6px; color: #795548; font-size: 0.875rem;">
          Разрешения роли <strong>God</strong> защищены от изменений.
        </div>

        <div v-if="loading" style="text-align: center; padding: 40px">
          Загрузка...
        </div>

        <div v-else style="display: flex; flex-direction: column; gap: 12px">
          <div 
            v-for="permission in allPermissions" 
            :key="permission.id"
            style="display: flex; align-items: center; gap: 12px; padding: 8px; border-bottom: 1px solid #eee"
          >
            <Checkbox 
              :binary="true" 
              :modelValue="hasPermission(permission.id)"
              :disabled="isGodRole"
              @update:modelValue="togglePermission(permission)"
            />
            <label :style="{ cursor: isGodRole ? 'default' : 'pointer', flex: 1 }">
              {{ getPermissionLabel(permission.name) }}
            </label>
          </div>

          <div v-if="allPermissions.length === 0" style="text-align: center; padding: 40px; color: #999">
            Нет доступных разрешений
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
